import {z} from 'zod';

import {omit} from 'ramda';

import type {EngineTechnicalData} from '@dms/api/new-vehicle';

import type {Nullish} from 'shared/web';

import {
  optionalDecimalInput,
  optionalNonNegativeIntInput,
  optionalText,
} from '../utils/catalogueUi';
import {getTechnicalDataFit, type EngineFuel} from '../utils/getTechnicalDataFit';
import {presentTechnicalData} from '../utils/presentTechnicalData';

export const engineTechnicalDataSchema = z.object({
  batteryCapacityWh: optionalDecimalInput,
  batteryCapacityAh: optionalDecimalInput,
  batteryVoltage: optionalDecimalInput,
  batteryType: optionalText,
  chargingAC: optionalDecimalInput,
  chargingDC: optionalDecimalInput,
  chargingTimeAC: optionalDecimalInput,
  chargingTimeDC: optionalNonNegativeIntInput,
  chargingConnectorType: optionalText,
  hasDpf: z.boolean(),
});
type EngineTechnicalDataValues = z.infer<typeof engineTechnicalDataSchema>;

export const toEngineTechnicalDataValues = (
  data: EngineTechnicalData | Nullish
): EngineTechnicalDataValues => ({
  batteryCapacityWh: data?.batteryCapacityWh ?? '',
  batteryCapacityAh: data?.batteryCapacityAh ?? '',
  batteryVoltage: data?.batteryVoltage ?? '',
  batteryType: data?.batteryType ?? '',
  chargingAC: data?.chargingAC ?? '',
  chargingDC: data?.chargingDC ?? '',
  chargingTimeAC: data?.chargingTimeAC ?? '',
  chargingTimeDC: data?.chargingTimeDC?.toString() ?? '',
  chargingConnectorType: data?.chargingConnectorType ?? '',
  hasDpf: data?.hasDpf === true,
});

/**
 * An unticked particulate filter sends no key: the checkbox cannot tell "no filter" from "not entered",
 * so saving an engine without technical data leaves it empty.
 */
export const toEngineTechnicalData = (
  values: EngineTechnicalDataValues,
  engine: EngineFuel
): EngineTechnicalData => ({
  ...(getTechnicalDataFit(engine).hasBattery
    ? presentTechnicalData(engineTechnicalDataSchema.shape, omit(['hasDpf'], values))
    : {}),
  ...(values.hasDpf ? {hasDpf: true} : {}),
});
